import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ListRange } from '@angular/cdk/collections';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ReplaySubject } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';

import { SelectionItem } from '@shared/models';
import { toggleScrollState } from '@shared/utils';
import { ScreenSizeService } from '@shared/services';
import { StaticMessage } from '@shared/enums';

@UntilDestroy()
@Component({
  selector: 'app-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchableSelectComponent
  implements OnInit, OnChanges, AfterViewInit
{
  //#region Inputs

  @Input() placeholder: string;
  @Input() localizationPlaceholder: string;
  @Input() options: SelectionItem[];
  @Input() value: any;
  @Input() isDisabled = false;
  // Opt-in, because for most callers an option's `isActive` describes the
  // entity rather than whether it may be picked. Where it does gate the
  // choice, the option is left out of the panel and an inactive selection
  // stays visible in the trigger, greyed out like a placeholder.
  @Input() shouldHideInactiveOptions = false;
  @Input() shouldHideArrow = false;
  @Input() selectControl = new FormControl();
  @Input() shouldIncludeNoneOption = false;
  @Input() virtualizationRenderedRange: ListRange = { start: 0, end: 5 };
  // This is necessary due to an Angular Material bug - initial value is not displayed
  // if the selected item is not in the initial virtualization range (10 items)
  @Input() disableVirtualization = false;

  //#endregion

  //#region Outputs

  @Output() selectionChanged = new EventEmitter<MatSelectChange>();

  //#endregion

  //#region Templates

  @ViewChild('select', { static: true })
  selectElement: MatSelect;

  @ViewChild('virtualScroll', { static: false })
  virtualScroll: CdkVirtualScrollViewport;

  //#endregion

  filteredOptions$ = new ReplaySubject<SelectionItem[]>(1);
  filterControl = new FormControl();
  optionSize = 48;
  panelHeight: number;
  isHandsetLandscape: boolean = false;
  filteredOptions: SelectionItem[];

  StaticMessage = StaticMessage;

  constructor(private screenSizeService: ScreenSizeService) {}

  //#region Getters

  /**
   * The options the panel offers. An inactive option is deliberately kept in
   * `options` so the trigger can still resolve its name, but it is not a choice
   * the user can make.
   */
  private get selectableOptions(): SelectionItem[] {
    return this.shouldHideInactiveOptions
      ? this.options.filter((option) => option.isActive !== false)
      : [...this.options];
  }

  //#endregion

  //#region Lifecycle Hooks

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.selectControl.setValue(this.value);
    }

    if (changes.isDisabled) {
      const action = this.isDisabled ? 'disable' : 'enable';
      this.selectControl[action]({ emitEvent: false, onlySelf: true });
    }

    this.filteredOptions$.next(this.selectableOptions);

    // Only call if virtualization is enabled and viewport exists
    if (!this.disableVirtualization && this.virtualScroll) {
      setTimeout(() => {
        this.virtualScroll.checkViewportSize();
      });
    }
  }

  ngOnInit(): void {
    // set initial selection
    this.selectControl.setValue(this.value);

    // add the initial list values
    this.filteredOptions$.next(this.selectableOptions);

    // listen for search field value changes
    this.filterControl.valueChanges
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.filterItems();
        })
      )
      .subscribe();

    // set panel height based on the number of filtered options
    this.filteredOptions$
      .pipe(
        tap((values) => {
          this.filteredOptions = values;
          this.panelHeight = this.computePanelHeight();
        })
      )
      .subscribe();

    // set panel height based on the screen orientation
    this.screenSizeService.isHandsetLandscape$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap((isHandsetLandscape) => {
          this.isHandsetLandscape = isHandsetLandscape;

          if (this.selectElement.panelOpen) {
            this.selectElement.close();
          }

          this.panelHeight = this.computePanelHeight();
        })
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    if (!this.disableVirtualization && this.virtualScroll) {
      this.virtualScroll.setRenderedContentOffset(0);
      this.virtualScroll.setRenderedRange(this.virtualizationRenderedRange);
    }
  }

  //#endregion

  onOpenedChanged(opened: boolean): void {
    if (this.disableVirtualization || !this.virtualScroll) {
      return;
    }

    if (opened) {
      // Force virtual scroll to render properly when panel opens
      setTimeout(() => {
        if (this.virtualScroll) {
          // Only check viewport size, don't force scroll operations
          this.virtualScroll.checkViewportSize();
        }
      });
      toggleScrollState(false);
    } else {
      // Reset scroll position when closing
      if (this.virtualScroll) {
        this.virtualScroll.scrollToIndex(0);
      }
      toggleScrollState(true);
    }
  }

  onSelectionChanged(event: MatSelectChange): void {
    this.selectionChanged.emit({ ...event, value: this.selectControl.value });
  }

  trackByFunction(_index: number, item: SelectionItem): string | number {
    return item.id;
  }

  //#region Protected methods

  protected filterItems(): void {
    if (!this.options) {
      return;
    }
    // get the search keyword
    let search = this.filterControl.value.fullTrim();

    if (!search) {
      this.filteredOptions$.next(this.selectableOptions);

      return;
    } else {
      search = search.toLowerCase();
    }

    // filter the values
    this.filteredOptions$.next(
      this.selectableOptions.filter(
        (x) => x.name.toLowerCase().indexOf(search) > -1
      )
    );
  }

  //#endregion

  //#region Private methods

  private computePanelHeight(): number {
    if (this.filteredOptions.length === 0) {
      return 0;
    }

    // count "None" as a visible option in the panel height computation
    const totalOptions =
      this.filteredOptions.length + (this.shouldIncludeNoneOption ? 1 : 0);

    if (this.isHandsetLandscape) {
      if (totalOptions < 2) {
        return this.optionSize * totalOptions;
      }

      return 96;
    }

    if (totalOptions < 5) {
      return this.optionSize * totalOptions;
    }

    return 240;
  }

  //#endregion
}
